'use server'

import type {
    AutomationTriggerEvent,
    SalesFlowActiveWindow,
    SalesFlowCondition,
    SalesFlowExitRules,
    SalesFlowGraphInput,
    SalesFlowHandle,
    SalesFlowNodeType,
    SalesFlowRunStatus,
    SalesFlowTriggerType,
    SalesFlowWaitKind,
} from '@mediardex/contracts'
import { apiRequest, ApiResponse } from '../request'

/** Fluxos de vendas (automação + follow-up). Plano em docs/sales-flows.md. */

export interface SalesFlowTrigger {
    id?: number
    type: SalesFlowTriggerType
    event?: AutomationTriggerEvent | null
    pipelineId?: number | null
    stageId?: number | null
    companyId?: number | null
    delayMinutes?: number
    config?: Record<string, any> | null
    conditions?: SalesFlowCondition[] | null
    conditionLogic?: 'AND' | 'OR'
}

export interface SalesFlowNode {
    id?: number
    key: string
    type: SalesFlowNodeType
    config: Record<string, any>
    positionX: number
    positionY: number
}

export interface SalesFlowEdge {
    id?: number
    fromKey: string
    handle: SalesFlowHandle
    toKey: string
}

export interface SalesFlow {
    id: number
    organizationId: number
    name: string
    description?: string | null
    active: boolean
    launchable: boolean
    departmentId?: number | null
    activeWindow?: SalesFlowActiveWindow | null
    exitRules?: SalesFlowExitRules | null
    reentryCooldownHours?: number | null
    priority: number
    version: number
    legacyRef?: string | null
    /** Rascunho do editor, ainda não publicado. */
    draft?: { settings?: SalesFlowSettingsData | null; graph: SalesFlowGraphInput } | null
    draftSavedAt?: string | null
    triggers: SalesFlowTrigger[]
    nodes?: SalesFlowNode[]
    edges?: SalesFlowEdge[]
    _count?: { nodes: number; runs: number }
    createdAt: string
    updatedAt: string
}

export interface SalesFlowSettingsData {
    name?: string
    description?: string | null
    launchable?: boolean
    departmentId?: number | null
    activeWindow?: SalesFlowActiveWindow | null
    exitRules?: SalesFlowExitRules | null
    reentryCooldownHours?: number | null
    priority?: number
}

export interface SalesFlowRunLog {
    id: number
    nodeKey: string
    handle?: SalesFlowHandle | null
    status: 'EXECUTED' | 'SKIPPED' | 'FAILED' | 'WAITING' | 'SIMULATED'
    channel?: string | null
    departmentId?: number | null
    messageId?: string | null
    detail?: string | null
    createdAt: string
}

export interface SalesFlowRun {
    id: number
    flowId: number
    flowVersion: number
    leadId: number
    dealId?: number | null
    chatId?: number | null
    status: SalesFlowRunStatus
    dryRun: boolean
    currentNodeKey?: string | null
    waitingFor?: SalesFlowWaitKind | null
    resumeAt?: string | null
    lastError?: string | null
    startedAt: string
    finishedAt?: string | null
    startedByUserId?: number | null
    flow?: { id: number; name: string }
    lead?: { id: number; name: string; phoneNumber: string } | null
    logs?: SalesFlowRunLog[]
    _count?: { logs: number }
}

export interface SalesFlowNodeStat {
    nodeKey: string
    status: string
    _count: { _all: number }
}

export interface LaunchableSalesFlow {
    id: number
    name: string
    description?: string | null
    departmentId?: number | null
}

export async function getSalesFlows(): Promise<ApiResponse<SalesFlow[]>> {
    return apiRequest<SalesFlow[]>('sales/flows', {})
}

export async function getSalesFlow(id: number): Promise<ApiResponse<SalesFlow>> {
    return apiRequest<SalesFlow>(`sales/flows/${id}`, {})
}

export async function getLaunchableSalesFlows(): Promise<ApiResponse<LaunchableSalesFlow[]>> {
    return apiRequest<LaunchableSalesFlow[]>('sales/flows/launchable', {})
}

export async function createSalesFlow(data: SalesFlowSettingsData & { name: string }): Promise<ApiResponse<SalesFlow>> {
    return apiRequest<SalesFlow>('sales/flows', { data: data as any }, 'post')
}

export async function updateSalesFlow(id: number, data: SalesFlowSettingsData): Promise<ApiResponse<SalesFlow>> {
    return apiRequest<SalesFlow>(`sales/flows/${id}`, { data: data as any }, 'patch')
}

export async function publishSalesFlow(id: number, graph: SalesFlowGraphInput): Promise<ApiResponse<{ flow: SalesFlow }>> {
    return apiRequest<{ flow: SalesFlow }>(`sales/flows/${id}/publish`, { data: graph as any }, 'post')
}

/** Salva o rascunho sem validar e sem mexer no que o motor executa. */
export async function saveSalesFlowDraft(
    id: number,
    draft: { settings: SalesFlowSettingsData; graph: SalesFlowGraphInput },
): Promise<ApiResponse<{ draftSavedAt: string }>> {
    return apiRequest<{ draftSavedAt: string }>(`sales/flows/${id}/draft`, { data: draft as any }, 'put')
}

export async function discardSalesFlowDraft(id: number): Promise<ApiResponse<SalesFlow>> {
    return apiRequest<SalesFlow>(`sales/flows/${id}/draft`, {}, 'delete')
}

export async function setSalesFlowActive(id: number, active: boolean): Promise<ApiResponse<SalesFlow>> {
    return apiRequest<SalesFlow>(`sales/flows/${id}/${active ? 'activate' : 'deactivate'}`, {}, 'post')
}

export async function deleteSalesFlow(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`sales/flows/${id}`, {}, 'delete')
}

export async function getSalesFlowStats(id: number): Promise<ApiResponse<SalesFlowNodeStat[]>> {
    return apiRequest<SalesFlowNodeStat[]>(`sales/flows/${id}/stats`, {})
}

export async function startSalesFlowRun(
    id: number,
    data: { leadId: number; dealId?: number; chatId?: number; dryRun?: boolean },
): Promise<ApiResponse<SalesFlowRun>> {
    return apiRequest<SalesFlowRun>(`sales/flows/${id}/runs`, { data }, 'post')
}

export async function getSalesFlowRuns(filters: {
    flowId?: number
    leadId?: number
    status?: SalesFlowRunStatus
    dryRun?: boolean
}): Promise<ApiResponse<SalesFlowRun[]>> {
    return apiRequest<SalesFlowRun[]>('sales/flows/runs', { params: filters as any })
}

export async function getSalesFlowRun(runId: number): Promise<ApiResponse<SalesFlowRun>> {
    return apiRequest<SalesFlowRun>(`sales/flows/runs/${runId}`, {})
}

/** Run em andamento do lead de uma conversa (faixa do chat). */
export interface ChatFlowRun {
    id: number
    status: SalesFlowRunStatus
    waitingFor?: SalesFlowWaitKind | null
    resumeAt?: string | null
    startedAt: string
    startedByUserId?: number | null
    chatId?: number | null
    /** No "Agendar envio": `{ message, scheduledAt }`. */
    triggerMetadata?: Record<string, any> | null
    flow: { id: number; name: string; systemKey?: string | null }
}

export async function getChatFlowRuns(chatId: number): Promise<ApiResponse<ChatFlowRun[]>> {
    return apiRequest<ChatFlowRun[]>(`sales/flows/runs/chat/${chatId}`, {})
}

export async function scheduleChatMessage(data: { chatId: number; message: string; scheduledAt: string }): Promise<ApiResponse<SalesFlowRun>> {
    return apiRequest<SalesFlowRun>('sales/flows/scheduled-messages', { data }, 'post')
}

export async function cancelSalesFlowRun(runId: number): Promise<ApiResponse<SalesFlowRun>> {
    return apiRequest<SalesFlowRun>(`sales/flows/runs/${runId}/cancel`, {}, 'post')
}
